/**
 * Text-only Paisley Business brand mark.
 * Pure SVG + text — no raster image, never blurs, scales perfectly.
 *
 * Composition: a hand-drawn paisley boteh ("wee paisley pattern") in sky blue,
 * sitting next to "Paisley Business" word-mark in mixed serif/sans typography
 * so it's visually distinct from the paisley.org.uk water-drop branding.
 */
export const BrandLogo = ({
  size = 'md',          // sm | md | lg
  variant = 'light',    // 'light' = on dark bg, 'dark' = on light bg
  className = '',
}) => {
  const sizes = {
    sm: { wrap: 'gap-1.5', icon: 'h-6 w-auto', paisley: 'text-2xl', business: 'text-[10px] tracking-[0.28em]' },
    md: { wrap: 'gap-2',    icon: 'h-8 w-auto', paisley: 'text-3xl', business: 'text-xs tracking-[0.32em]' },
    lg: { wrap: 'gap-2.5',  icon: 'h-11 w-auto', paisley: 'text-4xl sm:text-5xl', business: 'text-sm tracking-[0.36em]' },
  };
  const s = sizes[size] || sizes.md;
  const paisleyColor = variant === 'dark' ? 'text-slate-900' : 'text-white';
  const businessColor = variant === 'dark' ? 'text-slate-500' : 'text-slate-300';
  const accent = 'text-sky-400';
  const lineColor = variant === 'dark' ? 'bg-sky-500' : 'bg-sky-400';

  return (
    <span className={`inline-flex items-center select-none ${s.wrap} ${className}`} aria-label="Paisley Business">
      {/* Paisley boteh / teardrop */}
      <svg
        viewBox="0 0 48 56"
        className={`${s.icon} ${accent} flex-shrink-0`}
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {/* Outer boteh silhouette — tapered droplet with a curled tip */}
        <path d="M24 4 C40 6 44 22 36 34 C32 41 22 44 16 46 C10 48 9 52 14 52" />
        {/* Lower curl back round */}
        <path d="M14 52 C6 51 2 42 4 32 C6 20 12 10 22 5" />
        {/* Inner swirl decoration */}
        <path d="M22 18 C28 18 32 23 30 29 C28 33 23 33 21 30" strokeWidth="2" opacity="0.85" />
        {/* Tiny dot inside the swirl */}
        <circle cx="25" cy="25" r="1.6" fill="currentColor" stroke="none" />
      </svg>

      {/* Wordmark */}
      <span className="inline-flex flex-col leading-none">
        <span className={`font-heading font-bold tracking-tight ${s.paisley}`}>
          <span className={accent}>P</span>
          <span className={paisleyColor}>aisley</span>
        </span>
        <span className="inline-flex items-center gap-2 mt-1.5">
          <span className={`h-px w-4 ${lineColor}`} />
          <span className={`font-semibold uppercase ${s.business} ${businessColor}`}>
            Business
          </span>
          <span className={`h-px flex-1 ${lineColor} opacity-40`} />
        </span>
      </span>
    </span>
  );
};

export default BrandLogo;
